import { Box, Button } from "@mui/material";
import React, { useState, useRef, useEffect, useContext } from "react";
import QuizFeedback from "./QuizFeedback";
import QuizTimerStartOverlay from "overlay/quizComponentOverlay";
import LessonContext from "context/lessonContext/LessonContext";
import QuizTimer from "./TimerCard";
import NofityUserContext from "context/notifyUserContext/NotifyUserContext";
import { API_ENDPOINTS } from "config/ApiConfig";
import usePostRequest from "hooks/usePostRequest";
import QuizResultCard from "./QuizResultCard";
import VideoPlayerSkeleton from "components/common/skeleton/videoPlayerSkeleton";

// Constants for word limits
const MIN_WORDS = 1;
const MAX_WORDS = 1;

const HtmlCodeQuiz = ({ content }) => {
  const { lessonInfo, lessonIdentifiers, updateLessonCompletionData } = useContext(LessonContext);
  const { notifyUser } = useContext(NofityUserContext);
  const [display, setLoading] = useState(false);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [contentDetails, setContentDetails] = useState(null);
  const [contentDetails1, setContentDetails1] = useState(null);
  const questionRef = useRef(null);
  const htmlContentRef = useRef(null); // New ref for HTML content
  const [timerKey, setTimerKey] = useState(0);
  const [quizStatData, setQuizStatData] = useState(false);
  const [quizStatApiResponse, setQuizStatApiResponse] = useState(null);
  const [isQuizFeedBackStatNull, setIsQuizFeedBackStatNull] = useState(false);
  const [isQuizStatDetailsNull, setIsQuizStatDetailsNull] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [allInputsFilled, setAllInputsFilled] = useState(false);
  const [invalidCells, setInvalidCells] = useState(new Set());
  const [duplicateWords, setDuplicateWords] = useState(new Set());
  const [tableInitialized, setTableInitialized] = useState(false); // New state to track initialization

  const { data: quizStatResponse, postData: quizStatPayload } =
    usePostRequest(API_ENDPOINTS.FETCH_QUIZ_STAT);

  const { postData: saveQuizResponse } =
    usePostRequest(API_ENDPOINTS.SAVE_HTML_QUIZ_PROGRESS);

  // Helper function to count words in a string
  const countWords = (text) => {
    const trimmedText = text.trim();
    return trimmedText ? trimmedText.split(/\s+/).length : 0;
  };

  // First, add a function to identify the quiz type
  const identifyQuizType = () => {
    if (!contentDetails || !contentDetails.htmlCodeDetails) return 'unknown';
    
    const quizId = contentDetails.htmlCodeDetails.quiz_ht_id;
    const htmlContent = contentDetails.htmlCodeDetails.html_question || '';
    
    // Check for specific markers in the HTML content to identify quiz types
    if (htmlContent.includes('coloringBoxTable')) {
      return 'shading';
    } else if (htmlContent.includes('tic-tac-toe')) {
      return 'icando';
    } else {
      // Assuming this is the family/stranger quiz
      return 'familystranger';
    }
  };

  // Helper function to validate word count
  const isWordCountValid = (text) => {
    const wordCount = countWords(text);
    return wordCount === MIN_WORDS;
  };

  // Function to validate input and handle cell styling
  const validateWordCount = (cell) => {
    const text = cell.textContent.trim();
    const wordCount = countWords(text);
    const cellId = cell.getAttribute('data-cell-id') || cell.getAttribute('id') || 
                   `cell-${Math.random().toString(36).substr(2, 9)}`;
    
    // Ensure cell has an ID for tracking
    if (!cell.hasAttribute('data-cell-id') && !cell.getAttribute('id')) {
      cell.setAttribute('data-cell-id', cellId);
    }

    if (wordCount > MAX_WORDS) {
      const firstWord = text.split(/\s+/)[0];
      cell.textContent = firstWord;
      notifyUser(
        "Word Limit Exceeded",
        "Only one word is allowed per cell. Extra words have been removed.",
        "warning"
      );
    }

    if (!isWordCountValid(cell.textContent.trim())) {
      setInvalidCells(prev => new Set([...prev, cellId]));
      return false;
    } else {
      setInvalidCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellId);
        return newSet;
      });
      return true;
    }
  };

  // Handle keydown events to prevent spaces
  const handleKeyDown = (e) => {
    if (e.key === " ") {
      e.preventDefault();
    }
  };

  // Function to validate input
  const validateInput = (event) => {
    const cell = event.target;
    let text = cell.textContent;
    
    if (text !== text.trim()) {
      text = text.trim();
      cell.textContent = text;
    }
    
    const cleanText = text.replace(/[^a-zA-Z]/g, '');
    
    if (cleanText !== text) {
      event.preventDefault();
      cell.textContent = cleanText;
      notifyUser(
        "Invalid Input",
        "Only letters are allowed. Spaces, numbers and special characters will be removed.",
        "warning"
      );
    }

    validateWordCount(cell);
    checkInputFieldsFilled(); // Call it here to ensure immediate validation
  };

  // Modify checkInputFieldsFilled to be quiz-specific
  const checkInputFieldsFilled = () => {
    if (!htmlContentRef.current) return;
    
    const quizType = identifyQuizType();
    
    switch(quizType) {
      case 'shading':
        // For shading quiz, enable save if at least one cell is selected
        const selectedCells = htmlContentRef.current.querySelectorAll('#coloringBoxTable td.selected');
        setAllInputsFilled(selectedCells.length > 0);
        break;
        
      case 'icando':
        // For I Can Do quiz, enable save if all cells have content
        const icandoCells = htmlContentRef.current.querySelectorAll(
          '.html_structure_wrapper__tic-tac-toe__cell[contenteditable="true"]'
        );
        
        let allFilled = true;
        icandoCells.forEach(cell => {
          if (cell.textContent.trim() === '') {
            allFilled = false;
          }
        });
        
        setAllInputsFilled(allFilled);
        break;
        
      case 'familystranger':
        // Try different selectors to debug
        const allTableCells = htmlContentRef.current.querySelectorAll('table td');
        console.log('All table cells: ', allTableCells);
        
        let editableCells = htmlContentRef.current.querySelectorAll('table td[contenteditable="true"]');
        console.log('Editable cells: ', editableCells);
        
        // If no cells are found with contenteditable attribute, try getting all non-header cells
        if (editableCells.length === 0) {
          const nonHeaderCells = htmlContentRef.current.querySelectorAll('table td:not(th)');
          console.log('Non-header cells: ', nonHeaderCells);
          // Use these cells instead
          editableCells = nonHeaderCells;
        }
        
        console.log('--- ', editableCells)
        if (editableCells.length === 0) {
          setAllInputsFilled(false);
          return;
        }

        const enteredWords = new Set();
        let allValid = true;
        let hasDuplicates = false;
        const newDuplicates = new Set();

        // Check each editable cell
        editableCells.forEach((cell) => {
          const text = cell.textContent.trim().toLowerCase();
          
          if (text === "") {
            allValid = false;
            return;
          }
          
          if (!isWordCountValid(text)) {
            allValid = false;
            return;
          }
          
          if (enteredWords.has(text)) {
            newDuplicates.add(text);
            hasDuplicates = true;
          } else {
            enteredWords.add(text);
          }
        });
        
        setDuplicateWords(newDuplicates);
        setAllInputsFilled(allValid && !hasDuplicates && invalidCells.size === 0);
        break;
    }
  };

  // Modify setupTableCells to add event listeners for shading quiz
  const setupTableCells = () => {
    if (!htmlContentRef.current) {
      console.log("HTML content ref not available");
      return;
    }

    const quizType = identifyQuizType();
    console.log(`Setting up table cells for quiz type: ${quizType}`);

    if (quizType === 'shading') {
      // Get all cells in the shading table
      const tableCells = htmlContentRef.current.querySelectorAll('#coloringBoxTable td');
      console.log('Found shading table cells: ', tableCells);

      // Make cells clickable and add event listeners
      tableCells.forEach((cell) => {
        cell.addEventListener("click", handleShadingCellClick);
      });
    } else if (quizType === 'familystranger') {
      // Get all non-header table cells
      const tableCells = htmlContentRef.current.querySelectorAll('table td:not(th)');
      console.log('Found table cells: ', tableCells);

      // Make cells editable
      tableCells.forEach((cell, index) => {
        // Skip if cell already has content
        if (!cell.textContent.trim()) {
          cell.setAttribute('contenteditable', 'true');
        }

        // Add event listeners
        cell.addEventListener("input", validateInput);
        cell.addEventListener("blur", validateInput);
        cell.addEventListener("keydown", handleKeyDown);
      });
    }

    setTableInitialized(true);
    checkInputFieldsFilled();
  };

  const handleShadingCellClick = (event) => {
    const cell = event.target;

    // Toggle selection
    if (cell.classList.contains('selected')) {
      cell.classList.remove('selected');
      cell.style.border = ''; // Remove border color
    } else {
      cell.classList.add('selected');
      cell.style.border = '2px solid green'; // Change border to green when selected
    }

    // Check if at least one cell is selected
    checkShadingSelection();
  };

  // Modify checkShadingSelection to enable save button if at least one cell is selected
  const checkShadingSelection = () => {
    if (!htmlContentRef.current) return;

    // Get all selected cells
    const selectedCells = htmlContentRef.current.querySelectorAll('#coloringBoxTable td.selected');
    
    // Enable save button if at least one cell is selected
    setAllInputsFilled(selectedCells.length > 0);
  };

  const validateICanDoInput = (event) => {
    const cell = event.target;
    // For I Can Do quiz, just ensure it's not empty
    setAllInputsFilled(Array.from(
      htmlContentRef.current.querySelectorAll('.html_structure_wrapper__tic-tac-toe__cell[contenteditable="true"]')
    ).every(cell => cell.textContent.trim() !== ''));
  };

  useEffect(() => {
    setTimerKey(timerKey + 1);
  }, [quizStarted]);

  useEffect(() => {
    if (lessonInfo) {
      const foundContent = lessonInfo.find(item => item.content_id === content);
      if (foundContent) {
        setContentDetails(foundContent.contentDetails);
        setContentDetails1(foundContent);
      }
    }
  }, [lessonInfo, content]);

  useEffect(() => {
    if (contentDetails1 && !quizStatData && lessonIdentifiers?.course_id) {
      getQuizStatData(
        contentDetails1.RefContentType.name,
        contentDetails1.contentDetails.htmlCodeDetails.quiz_ht_id,
        lessonIdentifiers?.course_id
      );
      setQuizStatData(true);
    }
  }, [contentDetails1, lessonIdentifiers, quizStatPayload]);

  useEffect(() => {
    if (quizStatResponse && contentDetails1) {
      setQuizStatApiResponse(quizStatResponse);
      if (!quizStatResponse.quizfeedBackStat || quizStatResponse.quizfeedBackStat.length === 0) {
        setIsQuizFeedBackStatNull(true);
        setIsQuizStatDetailsNull(false);
        // updateLessonCompletionData(content);
      } else {
        setIsQuizFeedBackStatNull(false);
        setIsQuizStatDetailsNull(true);
      }

      if (!quizStatResponse.quizStatDetails || quizStatResponse.quizStatDetails.length === 0) {
        setIsQuizStatDetailsNull(true);
        setIsQuizFeedBackStatNull(true);
        setLoading(true);
      }
    }
  }, [quizStatResponse, contentDetails1]);

  useEffect(() => {
    if (quizStatApiResponse) {
      const combinedData = quizStatApiResponse.quizStatDetails.map((detail) => {
        const feedback = quizStatApiResponse.quizfeedBackStat.find(
          (fb) => fb.feedback.response_ht_id === detail.response_ht_id
        );

        return {
          id: detail.response_ht_id,
          question: detail.MasterQuestion ? detail.MasterQuestion.question : '',
          answer: detail.answer_text,
          submission_time: detail.submission_time,
          feedback_submission_time: feedback ? feedback.feedback.updated_on : null,
          question_score: detail.MasterQuestion ? detail.MasterQuestion.score : 0,
          feedback: feedback ? feedback.feedback.feedback_text : null,
          feedback_score: feedback ? feedback.feedback.score : null,
          completion_time: detail.completion_time,
        };
      });

      setModalData(combinedData);
    }
  }, [quizStatApiResponse]);

  useEffect(() => {
    if (quizStatResponse) {
      const { quizStatDetails } = quizStatResponse;
  
      if (Array.isArray(quizStatDetails) && quizStatDetails.length > 0) {
        console.log('quizStatDetails is non-empty:', quizStatDetails);
        updateLessonCompletionData(content);
      }

    }
  }, [quizStatResponse]);

  // Effect to set up table cells when HTML content is loaded
  useEffect(() => {
    if (quizStarted && htmlContentRef.current && !tableInitialized) {
      // Increase timeout to ensure table is rendered
      setTimeout(() => {
        console.log('HTML Content: ', htmlContentRef.current.innerHTML);
        setupTableCells();
      }, 1000);
    }
  }, [quizStarted, htmlContentRef.current, tableInitialized]);

  // Effect to force re-validation when dependent states change
  useEffect(() => {
    if (quizStarted && tableInitialized) {
      console.log("try 1")
      checkInputFieldsFilled();
    }
  }, [invalidCells, duplicateWords, tableInitialized]);

  // Clean up event listeners when component unmounts
  useEffect(() => {
    return () => {
      if (htmlContentRef.current) {
        const tableCells = htmlContentRef.current.querySelectorAll('table td[contenteditable="true"]');
        tableCells.forEach(cell => {
          cell.removeEventListener("input", validateInput);
          cell.removeEventListener("blur", validateInput);
          cell.removeEventListener("keydown", handleKeyDown);
        });
      }
    };
  }, []);

  const getQuizStatData = async (ref_content_type, quiz_id, course_id) => {
    const payLoad = {
      course_id: course_id,
      quiz__id: quiz_id,
      ref_content_type: ref_content_type,
    };

    try {
      await quizStatPayload(payLoad);
    } catch (error) {
      console.error("Error saving quiz response:", error);
    }
  };

  const handleStartQuiz = () => {
    setQuizStarted(true);
    setStartTime(new Date());
    setLoading(true);
    setTableInitialized(false); // Reset initialization state
    // Ensure button is disabled initially
    setAllInputsFilled(false);
  };

  // Modify handleSave to handle different quiz types
  const handleSave = async () => {
    const quizType = identifyQuizType();
    
    // Final validation
    checkInputFieldsFilled();
    
    if (!allInputsFilled) {
      switch(quizType) {
        case 'shading':
          notifyUser(
            "No Selection",
            "Please select at least one activity from the table before saving.",
            "error"
          );
          break;
          
        case 'icando':
          notifyUser(
            "Incomplete Form",
            "Please fill all cells with activities you can do.",
            "error"
          );
          break;
          
        case 'familystranger':
          if (duplicateWords.size > 0) {
            notifyUser(
              "Duplicate Words Found",
              "Duplicate entries not allowed for family and stranger quiz",
              "error"
            );
          } else if (invalidCells.size > 0) {
            notifyUser(
              "Invalid Entries",
              "Some cells contain invalid entries. Please make sure each cell has exactly one word.",
              "error"
            );
          } else {
            notifyUser(
              "Incomplete Form",
              "Please fill all cells with valid words before saving.",
              "error"
            );
          }
          break;
      }
      return;
    }

    setIsSaving(true);
    const htmlContent = htmlContentRef.current.innerHTML;
    const endTime = new Date(); 
    const timeTaken = (endTime - startTime) / 1000; 
    const timeTakenInMinutes = (timeTaken / 60).toFixed(2);
    
    let answers = [];
    
    // Collect answers based on quiz type
    switch(quizType) {
      case 'shading':
        const selectedCells = htmlContentRef.current.querySelectorAll('#coloringBoxTable td.selected');
        answers = Array.from(selectedCells).map(cell => cell.textContent.trim());
        break;
        
      case 'icando':
        const icandoCells = htmlContentRef.current.querySelectorAll(
          '.html_structure_wrapper__tic-tac-toe__cell[contenteditable="true"]'
        );
        answers = Array.from(icandoCells).map(cell => cell.textContent.trim());
        break;
        
      case 'familystranger':
        const editableCellsForSubmit = htmlContentRef.current.querySelectorAll(
          'table td[contenteditable="true"]'
        );
        answers = Array.from(editableCellsForSubmit).map(cell => cell.textContent.trim());
        break;
    }

    const payload = {
      course_id: lessonIdentifiers.course_id,
      quiz_ht_id: contentDetails.htmlCodeDetails.quiz_ht_id,
      question: contentDetails.questions[0].question_id,
      answer_text: htmlContent,
      completion_time: timeTakenInMinutes,
      answers: answers
    };

    const payloadArray = [payload];

    try {
      await saveQuizResponse(payloadArray);
      setQuizSubmitted(true);
      updateLessonCompletionData(content);
    } catch (error) {
      console.error("Error saving quiz:", error);
      notifyUser(
        "Error",
        "There was a problem saving your quiz. Please try again.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleTimeUp = async () => {
    if (questionRef.current) {
      questionRef.current.innerHTML = "";
    }
  };

    useEffect(() => {
      if (quizSubmitted) {
        getQuizStatData(contentDetails1.RefContentType.name,
          contentDetails1.contentDetails.htmlCodeDetails.quiz_ht_id,
          lessonIdentifiers?.course_id);
        const timer = setTimeout(() => {
          setQuizSubmitted(false);
          setLoading(false);
        }, 5000);
  
        return () => clearTimeout(timer);
      }
    }, [quizSubmitted]);

  // Check quiz submission and stat states before display check
  if (quizSubmitted) {
    return <QuizFeedback success={true} />;
  }

  if (!isQuizStatDetailsNull && isQuizFeedBackStatNull) {
    return (
      <QuizResultCard
        data={modalData}
        title={quizStatApiResponse.quizStatDetails[0].MasterQuizHtmlQuiz.quiz_name}
        date={quizStatApiResponse.quizStatDetails[0].submission_time}
        status="Submitted"
      />
    );
  }

  if (isQuizStatDetailsNull && !isQuizFeedBackStatNull) {
    return (
      <QuizResultCard
        data={modalData}
        title={quizStatApiResponse.quizStatDetails[0].MasterQuizHtmlQuiz.quiz_name}
        date={quizStatApiResponse.quizStatDetails[0].submission_time}
        score={quizStatApiResponse.quizfeedBackStat[0].feedback.score}
        status="Graded"
      />
    );
  }

  // Main quiz content
  return display ? (
    <>
      {!quizStarted ? (
        <QuizTimerStartOverlay handleStartQuiz={handleStartQuiz} />
      ) : (
        <Box className="AFQ_wrapper">
          {contentDetails.htmlCodeDetails.estimated_completion_time && (
            <Box className="AFQ_wrapper__time">
              <QuizTimer 
                key={timerKey} 
                estimatedCompletionTime={contentDetails.htmlCodeDetails.estimated_completion_time} 
                handleComplete={handleTimeUp}
              />
            </Box>
          )}
          <Box
            ref={questionRef}
            className="AFQ_wrapper__long-question-wrapper"
            dangerouslySetInnerHTML={{
              __html: contentDetails.questions[0]?.question.replace(/&nbsp;/g, " ") || "",
            }}
          />
          <Box
            ref={htmlContentRef}
            className="AFO_wrapper__answer-wrapper"
            dangerouslySetInnerHTML={{
              __html: contentDetails.htmlCodeDetails.html_question.replace(/&nbsp;/g, " ") || "",
            }}
          />
          <Box
            className="SAQ_wrapper-navigate"
            sx={{ display: "flex", justifyContent: "flex-end", marginTop: "20px", paddingTop: "20px" }}
          >
            <Button
              className="SAQ_wrapper-navigate__button"
              variant="outlined"
              onClick={handleSave}
              disabled={isSaving || !allInputsFilled}
            >
             {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      )}
    </>
  ) : (
    <VideoPlayerSkeleton />
  );
};

export default HtmlCodeQuiz;